import { makeStyles } from 'tss-react/mui'
import React, { useCallback, useImperativeHandle, useRef, useState } from 'react'
import ArrowRight from '@mui/icons-material/ArrowRight'
import {
  ListItemIcon,
  Menu,
  MenuItem,
  MenuItemProps,
  MenuProps,
  PopoverPosition,
} from '@mui/material'
import clsx from 'clsx'
import * as _ from 'lodash'
import { SvgComponent } from 'ts/types/commonTable.types'

export interface AgNestedMenuItemProps extends Omit<MenuItemProps, 'button'> {
  parentMenuOpen: boolean
  component?: React.ElementType
  leftIcon?: string | SvgComponent
  label?: React.ReactNode
  rightIcon?: React.ReactNode
  ContainerProps?: React.HTMLAttributes<HTMLElement> & React.RefAttributes<HTMLElement | null>
  MenuProps?: Omit<MenuProps, 'children'>
  button?: true
}

const TRANSPARENT = 'rgba(0,0,0,0)'
const useMenuItemStyles = makeStyles<{ open: boolean }>()((theme, { open }) => ({
  root: {
    backgroundColor: open ? theme.palette.action.hover : TRANSPARENT,
  },
}))

const AgNestedMenuItem = React.forwardRef((props: AgNestedMenuItemProps, ref) => {
  const {
    parentMenuOpen,
    leftIcon,
    label,
    rightIcon = <ArrowRight />,
    children,
    className,
    tabIndex: tabIndexProp,
    ContainerProps: ContainerPropsProp = {},
  } = props

  const { ref: containerRefProp, ...ContainerProps } = ContainerPropsProp

  const menuItemRef = useRef<HTMLLIElement>(null)
  useImperativeHandle(ref, () => menuItemRef.current)

  const containerRef = useRef<HTMLDivElement>(null)
  useImperativeHandle(containerRefProp, () => containerRef.current as HTMLElement)

  const menuContainerRef = useRef<HTMLDivElement>(null)

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false)

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setIsSubMenuOpen(true)

    if (ContainerProps?.onMouseEnter) {
      ContainerProps.onMouseEnter(event)
    }
  }
  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    setIsSubMenuOpen(false)

    if (ContainerProps?.onMouseLeave) {
      ContainerProps.onMouseLeave(event)
    }
  }

  const isSubmenuFocused = () => {
    const active = containerRef.current?.ownerDocument?.activeElement
    for (const child of Array.from(menuContainerRef.current?.children ?? [])) {
      if (child === active) {
        return true
      }
    }
    return false
  }

  const handleFocus = (event: React.FocusEvent<HTMLElement>) => {
    if (event.target === containerRef.current) {
      setIsSubMenuOpen(true)
    }

    if (ContainerProps?.onFocus) {
      ContainerProps.onFocus(event)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      return
    }

    if (isSubmenuFocused()) {
      event.stopPropagation()
    }

    const active = containerRef.current?.ownerDocument?.activeElement

    if (event.key === 'ArrowLeft' && isSubmenuFocused() && containerRef.current) {
      containerRef.current.focus()
    }

    if (
      event.key === 'ArrowRight' &&
      event.target === containerRef.current &&
      event.target === active
    ) {
      const firstChild = menuContainerRef.current?.children[0] as HTMLElement | undefined
      if (firstChild) {
        firstChild.focus()
      }
    }
  }

  const open = isSubMenuOpen && parentMenuOpen
  const menuItemClasses = useMenuItemStyles({ open })

  let tabIndex
  if (!props.disabled) {
    tabIndex = tabIndexProp ?? -1
  }

  const getanchorPosition = useCallback((): PopoverPosition => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      return {
        top: rect.top,
        left: rect.right,
      }
    } else {
      return {
        top: 0,
        left: 0,
      }
    }
  }, [containerRef.current])

  const LeftIcon: SvgComponent | string | undefined = leftIcon

  const renderLeftIcon = () => {
    if (typeof leftIcon === 'string') {
      return <img src={leftIcon} alt={label as string} />
    }
    if (LeftIcon) {
      return <LeftIcon className="highlightIcon" />
    }
    return null
  }

  return (
    <div
      {...ContainerProps}
      ref={containerRef}
      onFocus={handleFocus}
      tabIndex={tabIndex}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
      role="menuitem"
      aria-haspopup="true"
      aria-expanded={open}
    >
      <MenuItem
        {..._.omit(props, [
          'parentMenuOpen',
          'leftIcon',
          'rightIcon',
          'label',
          'ContainerProps',
          'MenuProps',
        ])}
        className={clsx(menuItemClasses.classes.root, className)}
        ref={menuItemRef}
      >
        {leftIcon && <ListItemIcon className="list-item-icon">{renderLeftIcon()}</ListItemIcon>}
        {label}
        {typeof leftIcon === 'string' && rightIcon}
      </MenuItem>
      <Menu
        style={{ pointerEvents: 'none' }}
        anchorReference="anchorPosition"
        anchorPosition={getanchorPosition()}
        open={open}
        autoFocus={false}
        disableAutoFocus
        disableEnforceFocus
        onClose={() => {
          setIsSubMenuOpen(false)
        }}
      >
        <div ref={menuContainerRef} style={{ pointerEvents: 'auto' }}>
          {children}
        </div>
      </Menu>
    </div>
  )
})

export default AgNestedMenuItem
